import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { TerminalReader } from '@frontend/payments-terminal-controller';
import { theme } from '@frontend/theme';
import {
  Button,
  ConfirmationModal,
  ContentLoader,
  styles as dsStyles,
  Heading,
  Chip,
  Text,
  ModalControlModalProps,
} from '@frontend/design-system';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useScopedTrackingIds } from '../../../hooks/use-scoped-tracking-ids';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { AddReaderCard, ReaderCard } from './select-terminal-step/reader-card';

const styles = {
  body: css``,
  footer: css`
    display: flex;
    gap: ${theme.spacing(2)};
    align-items: center;
    justify-content: flex-end;
  `,
  headerSection: css`
    width: 100%;
    display: flex;
    align-items: center;
    margin-bottom: ${theme.spacing(2)};
    > *:not(:first-child) {
      margin-left: ${theme.spacing(2)};
    }
  `,
  readerSection: css`
    height: 100%;
    display: flex;
    gap: ${theme.spacing(2)};
    flex-wrap: wrap;
    overflow-y: auto;
    ${dsStyles.smallScrollbar(theme)}
  `,
  terminalPaymentsContainer: css`
    min-height: ${theme.spacing(40)};
    display: grid;
    grid-template-rows: auto 1fr;
    overflow-y: hidden;
    align-items: center;
  `,
  locationChip: css`
    max-width: 200px;
  `,
};

export type SelectTerminalStepProps = {
  className?: string;
  readers: TerminalReader[];
  isReadersLoading: boolean;
  showLocationChip?: boolean;
  locationName?: string;
  confirmModalProps: ModalControlModalProps;
  canRegisterTerminals: boolean;
  onGoBack: () => void;
  onClickRegisterTerminal: () => void;
  onClickSelectReader: (reader: TerminalReader) => Promise<void>;
  refetchReaders: () => void;
  onClickConfirmPayment: () => void;
};

export const SelectTerminalStep = ({
  readers,
  locationName,
  showLocationChip,
  isReadersLoading,
  confirmModalProps,
  canRegisterTerminals,
  className,
  onGoBack,
  onClickSelectReader,
  onClickRegisterTerminal,
  refetchReaders,
  onClickConfirmPayment,
}: SelectTerminalStepProps) => {
  const { activeGateway } = useCollectPaymentMultiContext();

  const { t } = useTranslation('payments');

  const { backTrackingId, registerTerminalTrackingId, refreshTerminalTrackingId } = useScopedTrackingIds({
    backTrackingId: `${PAYMENT_MULTI_STEPS.selectTerminal}--${activeGateway}--back-btn`,
    registerTerminalTrackingId: `${PAYMENT_MULTI_STEPS.selectTerminal}--${activeGateway}--register-terminal-btn`,
    refreshTerminalTrackingId: `${PAYMENT_MULTI_STEPS.selectTerminal}--${activeGateway}--refresh-terminal-btn`,
  });

  return (
    <>
      <StyledStep
        id={PAYMENT_MULTI_STEPS.selectTerminal}
        label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.selectTerminal]}
        css={styles.body}
        className={className}
        hasInternalFooter
        components={{
          Footer: () => (
            <>
              {readers.length > 0 ? (
                <div className='step__footer' css={styles.footer}>
                  <Button
                    variant='tertiary'
                    style={{ marginRight: 'auto' }}
                    onClick={onGoBack}
                    trackingId={backTrackingId}
                  >
                    {t('Back')}
                  </Button>
                  <Button
                    variant='secondary'
                    onClick={() => {
                      refetchReaders();
                    }}
                    trackingId={refreshTerminalTrackingId}
                    disabled={isReadersLoading}
                    iconName='update'
                  >
                    {isReadersLoading ? t('Refreshing Terminals') : t('Refresh Terminals')}
                  </Button>
                </div>
              ) : (
                <div className='step__footer' css={styles.footer}>
                  <Button variant='tertiary' style={{ marginRight: 'auto' }} onClick={onGoBack}>
                    {t('Back')}
                  </Button>

                  {canRegisterTerminals && (
                    <Button
                      onClick={onClickRegisterTerminal}
                      trackingId={registerTerminalTrackingId}
                      disabled={isReadersLoading}
                    >
                      {t('Register a Terminal')}
                    </Button>
                  )}
                </div>
              )}
            </>
          ),
        }}
      >
        <div css={{ position: 'relative' }}>
          <ContentLoader show={isReadersLoading} message={t('Discovering terminals...')} />
          <div css={styles.terminalPaymentsContainer}>
            <section css={styles.headerSection}>
              <Heading level={3}>{t('Select a terminal')}</Heading>
              {showLocationChip && <Chip.SingleChip css={styles.locationChip}>{locationName}</Chip.SingleChip>}
            </section>
            {!isReadersLoading && readers.length > 0 ? (
              <section css={styles.readerSection}>
                {readers.map((reader) => (
                  <ReaderCard
                    key={reader.id}
                    reader={reader}
                    isConnected={false}
                    onClick={(reader) => onClickSelectReader(reader)}
                  />
                ))}
                {canRegisterTerminals && <AddReaderCard onClick={onClickRegisterTerminal} />}
              </section>
            ) : (
              <Text weight='bold' textAlign='center' css={{ width: '100%' }}>
                {t('No terminals registered for this account.')}
              </Text>
            )}
          </div>
        </div>
      </StyledStep>

      <ConfirmationModal
        {...confirmModalProps}
        title={t('This terminal is in use')}
        message={t('Override terminal payment, or select another terminal.')}
        onConfirm={onClickConfirmPayment}
        confirmLabel={t('Override Terminal Payment')}
        onCancel={confirmModalProps.onClose}
      />
    </>
  );
};
